
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useUser } from '@clerk/react';
import { IdleStopPolicy, Organization, OrganizationContextType, OrganizationMember, RoleType } from '@/types/organization';
import { STORAGE_KEYS } from '@/utils/seedMockData';
import { authService, type MeResponse } from '@/services/authService';
import { useKeyedAsync } from '@/hooks/useKeyedAsync';
import { organizationService } from '@/services/organizationService';

const OrganizationContext = createContext<OrganizationContextType | null>(null);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

/** A JSON value cached in localStorage, or null when absent or unreadable. */
const readCached = <T,>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

interface OrganizationProviderProps {
  children: React.ReactNode;
}

export const OrganizationProvider = ({ children }: OrganizationProviderProps) => {
  const { user } = useUser();
  // Initial paint: hydrate from localStorage so the UI has something to show
  // while the API call is in flight (or while the user is signed out).
  const [organizations, setOrganizations] = useState<Organization[]>(
    () => readCached<Organization[]>('gizmodata-organizations') ?? []);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(() => {
    const cachedOrgs = readCached<Organization[]>('gizmodata-organizations');
    if (!cachedOrgs) return null;
    return readCached<Organization>('gizmodata-current-organization') ?? cachedOrgs[0] ?? null;
  });
  // The user the authoritative /organizations/ fetch last completed for. `orgsLoaded` (false
  // until that fetch has completed for the signed-in user) gates the "first-time, create an
  // org" prompt so it can't flash during the fetch window (when `organizations` is briefly
  // empty — especially now that we purge the stale localStorage cache).
  const [orgsLoadedFor, setOrgsLoadedFor] = useState<string | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>(
    () => readCached<OrganizationMember[]>('gizmodata-members') ?? []);
  // /auth/me, stored with the user it was fetched for — everything below is computed from it, so a
  // sign-out or a different user never shows the previous user's answer (and no state is reset
  // inside an effect). One round trip: identity + scoped roles + is_system_admin + the
  // deployment policy. A 200 means the user is registered (incl. just-in-time provisioned under
  // SELF_SERVE_SIGNUP); a 403 means authenticated-but-not-registered.
  const [meResult, setMeResult] = useState<{
    userId: string; status: 'registered' | 'unregistered' | 'error'; me: MeResponse | null;
  } | null>(null);
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    authService
      .me()
      .then((me) => {
        if (!cancelled) setMeResult({ userId, status: me ? 'registered' : 'error', me });
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        // Only a definitive 403 means "not registered"; anything else is a
        // transient failure we don't want to hard-block on.
        setMeResult({ userId, status: /^HTTP 403\b/.test(message) ? 'unregistered' : 'error', me: null });
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);
  const meForUser = userId && meResult?.userId === userId ? meResult : null;
  const me = meForUser?.me ?? null;
  // Detect system-admin status (a SYSTEM-scope role). Only system admins should see the 'system'
  // scope option in ScopeSelector / Register* dialogs. The API enforces this server-side too;
  // this is pure UI-affordance hiding.
  const isSystemAdmin = me?.isSystemAdmin ?? false;
  // Backend registration status, derived from /auth/me. 'unregistered' means the
  // user authenticated with Clerk but has no users-table row (and the deployment
  // doesn't self-serve provision them) — the API 403s every call. We surface this
  // so the app can show a clear "account not provisioned" screen instead of
  // walking them into the org-creation flow that would just 403 on submit. Only a
  // definitive 403 flips to 'unregistered'; transient errors stay 'error' so a
  // network blip doesn't lock out a legitimate user.
  const registrationStatus: 'loading' | 'registered' | 'unregistered' | 'error' = meForUser?.status ?? 'loading';
  // Deployment-level: whether a NEW org may select self-managed (BYO) infra.
  // Folds in the ALLOW_SELF_MANAGED_INFRASTRUCTURE kill switch + billing mode +
  // the system-admin carve-out (server-computed on /auth/me). Default true so we
  // fail OPEN (the server still enforces it on create) if /auth/me is unavailable.
  const selfManagedInfrastructureAllowed = me?.selfManagedInfrastructureAllowed ?? true;
  // Whether THIS user may create an org (false for non-system-admins on a
  // customer-hosted control plane). Default true = fail open; the server 403s.
  const organizationCreationAllowed = me?.organizationCreationAllowed ?? true;
  // Whether a user's first organization needs a signup-invite code (a system-admin switch).
  const signupInviteRequired = me?.signupInviteRequired ?? false;
  // Deployment-level billing flag from /auth/me — when false (self-hosted /
  // customer-hosted, e.g. Summation), the portal hides billing/plan UI entirely.
  const billingEnabled = me?.billingEnabled ?? false;
  // Global compute cost markup from /auth/me (1.0 self-hosted, e.g. 3.0 Public Cloud).
  const computeCostMultiplier = me?.computeCostMultiplier ?? 1.0;
  const storagePricePerGbMonth = me?.storagePricePerGbMonth ?? 0;
  const portalBannerText = me?.portalBannerText ?? null;
  const grafanaBaseUrl = me?.grafanaBaseUrl ?? null;
  const portalBannerColor = me?.portalBannerColor ?? 'amber';
  // Guided tour: offer it by itself only once /auth/me has answered and the user has neither
  // completed nor dismissed it (an older API without the fields reads as "never offer").
  const autoTerminateAvailable = me?.autoTerminateEnabled ?? false;
  // Idle stop (stop a cluster that has run no statement for N minutes): hidden until /auth/me says
  // the job runs here.
  const idleStopPolicy = useMemo<IdleStopPolicy>(() => ({
    enabled: me?.idleStopEnabled ?? false,
    onByDefault: me?.idleStopOnByDefault ?? false,
    defaultMinutes: me?.idleStopDefaultMinutes ?? 15,
    minMinutes: me?.idleStopMinMinutes ?? 5,
    maxMinutes: me?.idleStopMaxMinutes ?? 1440,
    requiredMaxMinutes: me?.idleStopRequiredMaxMinutes ?? 60,
  }), [me]);
  const tourAutoStart = me !== null && me.tourCompletedAt === null && me.tourDismissedAt === null;

  // The current user's effective role in the current organization, resolved by
  // the API's hierarchical permission resolver (GET /auth/effective-role) — the
  // authoritative source. The server applies the system-admin override and role
  // inheritance (a SYSTEM-scope admin acts on any org; an org role flows down),
  // so the UI no longer re-derives roles from the localStorage `members` list
  // (which is not API-synced for the current user). null ⇒ no access here.
  // Keyed by user + org: after an org switch the previous org's role is never shown while the new
  // one loads — it reads as "no access" until it arrives. (Not keyed by the admin flag: /auth/me
  // landing after the org would otherwise blank the role mid-page; the flag only feeds the
  // old-API fallback below, read when the lookup fails.)
  const currentUserRole = useKeyedAsync<RoleType | null>(
    userId && currentOrganization?.id ? `${userId}:${currentOrganization.id}` : null,
    () => authService
      .effectiveRole({ organizationId: currentOrganization!.id })
      .then((res) => res?.role ?? null)
      // Older API without the endpoint: fall back to the system-admin flag so
      // platform admins keep their affordances; everyone else is gated safely.
      .catch(() => (isSystemAdmin ? 'admin' : null)),
    null,
  );

  // Once Clerk is signed in (so apiClient has a JWT to send), pull the
  // authoritative org list from /api/organizations/ and replace the
  // localStorage-cached version. A SUCCESSFUL response is authoritative even
  // when empty: [] means "you have access to no orgs", so we clear the stale
  // cache instead of keeping it. (Previously an empty result early-returned and
  // left seeded/mock orgs from localStorage on screen — which looked like the
  // user could see every org when the API had actually returned nothing.)
  // Only genuine FAILURES fall through to keeping the cache, so the UI doesn't
  // blank out on a transient error; anonymous loads keep the cache too.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    organizationService
      .list()
      .then((orgs) => {
        if (cancelled) return;
        setOrganizations(orgs);
        // If our cached current-org isn't in the fresh list (renamed,
        // deleted, lost access, or none at all), snap to the first available
        // org or clear it.
        // Adopt the FRESH object for the same org, not the cached one: its plan
        // follows the Stripe subscription, so a cached copy goes stale server-side.
        setCurrentOrganization((prev) =>
          (prev && orgs.find((o) => o.id === prev.id)) || orgs[0] || null);
        // Purge the cache when the authoritative list is empty so a stale
        // mock/seeded cache can't re-hydrate on the next paint.
        if (orgs.length === 0) {
          localStorage.removeItem('gizmodata-organizations');
          localStorage.removeItem('gizmodata-current-organization');
        }
      })
      .catch((err) => {
        console.warn('[OrganizationContext] Failed to fetch /organizations/ — keeping localStorage cache', err);
      })
      .finally(() => {
        if (!cancelled) setOrgsLoadedFor(userId);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);
  const orgsLoaded = Boolean(userId) && orgsLoadedFor === userId;

  // Save to localStorage whenever data changes
  // Only save if we have data (prevents overwriting seeded data with empty arrays on mount)
  useEffect(() => {
    if (organizations.length > 0) {
      localStorage.setItem('gizmodata-organizations', JSON.stringify(organizations));
    }
  }, [organizations]);

  useEffect(() => {
    if (currentOrganization) {
      localStorage.setItem('gizmodata-current-organization', JSON.stringify(currentOrganization));
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (members.length > 0) {
      localStorage.setItem('gizmodata-members', JSON.stringify(members));
    }
  }, [members]);

  // Invites are no longer client-mock state (GS-016 moved them to the real API);
  // purge any stale cache from the old localStorage-mock era.
  useEffect(() => {
    localStorage.removeItem('gizmodata-invites');
  }, []);

  // Ensure the current user is a member of the current organization (computed, not stored): this
  // handles mock data with a different email, or a user who just created their first org. They're
  // listed as admin when they created the org or it's the mock/demo org (createdByUserId 'mock-…').
  const userEmail = user?.primaryEmailAddress?.emailAddress;
  const membersWithSelf = useMemo(() => {
    if (!currentOrganization || !userEmail || !userId) return members;
    const existingMember = members.some(
      (m) => m.organizationId === currentOrganization.id && m.email === userEmail);
    const isCreator = currentOrganization.createdByUserId === userId;
    const isMockOrg = currentOrganization.createdByUserId?.startsWith('mock-');
    if (existingMember || !(isCreator || isMockOrg)) return members;
    const self: OrganizationMember = {
      id: `${currentOrganization.id}-${userId}-member`,
      organizationId: currentOrganization.id,
      userId,
      email: userEmail,
      role: 'admin',
      joinedAt: currentOrganization.createdAt,
    };
    return [...members, self];
  }, [currentOrganization, userEmail, userId, members]);

  const createOrganization = async (
    name: string,
    domain: string,
    defaultCloudRegionId: string,
    planId?: string,
    infraFlags?: { allowSystemInfrastructure?: boolean; allowSelfManagedInfrastructure?: boolean },
    signupInviteCode?: string,
    termsAccepted?: boolean,
    billingExempt?: boolean,
  ) => {
    // Enforce the at-least-one invariant client-side too — matches the API's
    // ck_organizations_infra_flags_at_least_one CHECK. Default both true.
    const allowSystemInfrastructure = infraFlags?.allowSystemInfrastructure ?? true;
    const allowSelfManagedInfrastructure = infraFlags?.allowSelfManagedInfrastructure ?? true;
    if (!allowSystemInfrastructure && !allowSelfManagedInfrastructure) {
      throw new Error('At least one of allowSystemInfrastructure or allowSelfManagedInfrastructure must be true.');
    }

    // Authoritative create against the API — NO optimistic/offline fallback. A
    // failed create must throw (callers surface it as a toast), not fabricate a
    // phantom org that exists only in the UI/localStorage with a synthetic
    // Date.now() id — that made failed creates look successful and left orgs in
    // the selector that aren't in the database. `planId` is passed through as-is
    // (omitted when not chosen) so the server assigns the Free plan; sending a
    // synthetic MOCK plan id 400s on the FK.
    const createdOrg = await organizationService.create({
      name,
      label: name, // Use name as label
      domain,
      planId,
      defaultCloudRegionId,
      allowSystemInfrastructure,
      allowSelfManagedInfrastructure,
      signupInviteCode,
      termsAccepted,
      billingExempt,
    });

    // Add creator as admin member (local view; the authoritative member list
    // comes from the API). Keyed on the REAL org id returned by the server.
    if (user?.primaryEmailAddress?.emailAddress) {
      const newMember: OrganizationMember = {
        id: `${Date.now()}-member`,
        organizationId: createdOrg.id,
        userId: user.id,
        email: user.primaryEmailAddress.emailAddress,
        role: 'admin',
        joinedAt: new Date().toISOString(),
      };
      setMembers(prev => [...prev, newMember]);
    }

    setOrganizations(prev => [...prev, createdOrg]);
    setCurrentOrganization(createdOrg);
    return createdOrg;
  };

  const handleSetCurrentOrganization = (org: Organization) => {
    setCurrentOrganization(org);
  };

  /** Record acceptance of the current Terms of Service for the current organization (admins). */
  const acceptCurrentTerms = async () => {
    if (!currentOrganization) return;
    const updated = await organizationService.acceptTerms(currentOrganization.id);
    setOrganizations((prev) => prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o)));
    setCurrentOrganization((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
  };

  const updateOrganization = (
    id: string,
    updates: {
      name?: string;
      label?: string;
      domain?: string;
      defaultCloudRegionId?: string | null;
      planId?: string;
      allowSystemInfrastructure?: boolean;
      allowSelfManagedInfrastructure?: boolean;
      disableCrossCloudCatalogMounts?: boolean;
      disableCrossRegionCatalogMounts?: boolean;
    },
  ) => {
    setOrganizations(prev => prev.map(org => {
      if (org.id === id) {
        // Compute the post-update infra-flag state and enforce the invariant.
        const nextAllowSystem = updates.allowSystemInfrastructure ?? org.allowSystemInfrastructure ?? true;
        const nextAllowSelf = updates.allowSelfManagedInfrastructure ?? org.allowSelfManagedInfrastructure ?? true;
        if (!nextAllowSystem && !nextAllowSelf) {
          throw new Error('At least one of allowSystemInfrastructure or allowSelfManagedInfrastructure must be true.');
        }

        const updatedOrg = {
          ...org,
          ...(updates.name && { name: updates.name }),
          ...(updates.label && { label: updates.label }),
          ...(updates.domain && { domain: updates.domain }),
          // null = the default was cleared; undefined = untouched.
          ...(updates.defaultCloudRegionId !== undefined && { defaultCloudRegionId: updates.defaultCloudRegionId ?? undefined }),
          ...(updates.allowSystemInfrastructure !== undefined && { allowSystemInfrastructure: updates.allowSystemInfrastructure }),
          ...(updates.allowSelfManagedInfrastructure !== undefined && { allowSelfManagedInfrastructure: updates.allowSelfManagedInfrastructure }),
          ...(updates.disableCrossCloudCatalogMounts !== undefined && { disableCrossCloudCatalogMounts: updates.disableCrossCloudCatalogMounts }),
          ...(updates.disableCrossRegionCatalogMounts !== undefined && { disableCrossRegionCatalogMounts: updates.disableCrossRegionCatalogMounts }),
          updatedAt: new Date().toISOString(),
          updatedByUserId: user?.id || 'unknown',
        };
        // Update current organization if it's the one being edited
        if (currentOrganization?.id === id) {
          setCurrentOrganization(updatedOrg);
        }
        return updatedOrg;
      }
      return org;
    }));
  };

  // Invitations moved to the real API (GS-016) — see inviteService,
  // InviteMemberDialog, and PendingInvitesCard. No client-side mock here.

  // Permission methods based on role hierarchy: admin > writer > reader
  const canInviteMembers = () => currentUserRole === 'admin';
  const canCreateDatabases = () => currentUserRole === 'admin' || currentUserRole === 'writer';
  const canCreateClusters = () => currentUserRole === 'admin' || currentUserRole === 'writer';
  const canViewDatabases = () => currentUserRole !== null; // All roles can view
  const canViewClusters = () => currentUserRole !== null; // All roles can view
  const canRunQueries = () => currentUserRole !== null; // All roles can run queries

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        organizations,
        orgsLoaded,
        registrationStatus,
        billingEnabled,
        computeCostMultiplier,
        storagePricePerGbMonth,
        portalBannerText,
        grafanaBaseUrl,
        portalBannerColor,
        tourAutoStart,
        autoTerminateAvailable,
        idleStopPolicy,
        members: membersWithSelf,
        currentUserRole,
        setCurrentOrganization: handleSetCurrentOrganization,
        createOrganization,
        acceptCurrentTerms,
        updateOrganization,
        canInviteMembers,
        canCreateDatabases,
        canCreateClusters,
        canViewDatabases,
        canViewClusters,
        canRunQueries,
        isSystemAdmin,
        selfManagedInfrastructureAllowed,
        organizationCreationAllowed,
        signupInviteRequired,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};
